import { useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Collapse,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
} from 'antd';
import locale from 'antd/es/date-picker/locale/ru_RU';
import 'dayjs/locale/ru.js';
import { useParams } from 'react-router-dom';
import { useStore, useUnit } from 'effector-react';
import { CloseOutlined } from '@ant-design/icons';
import { date_format_short } from '../../constants/date_format.js';
import { editProjectStagesFx } from '../../models/viewProjectModel/index.js';
import style from './EditProjectById.module.css';
import StageSelect from './StageSelect.jsx';
import {
  $dictionariesMaps,
  $dictionariesStore,
} from '../../models/dictionariesModel/index.js';
import { prepareProjectStagesDictionariesData } from '../../counters/viewProject/index.js';
import {
  $participantsMap,
  completePhaseEv,
  completePhaseFx,
  inProgressPhaseEv,
  pausePhaseEv,
  pausePhaseFx,
} from '../../models/projectCreateEditModel/index.js';
import ResponsibleSelects from './ResponsibleSelects.jsx';

const phaseStatusDict = [
  {
    label: 'Не начат',
    value: 'NOT_STARTED',
  },
  {
    label: 'В работе',
    value: 'IN_PROGRESS',
  },
  {
    label: 'Завершен',
    value: 'COMPLETED',
  },
  {
    label: 'Приостановлен',
    value: 'PAUSED',
  },
];

function EditSteps({ participantsTableData, stages, phases }) {
  const { id } = useParams();
  const isLoading = useStore(editProjectStagesFx.pending);
  const [form] = Form.useForm();

  const dictionariesStore = useStore($dictionariesStore);
  const { rolesMap } = useStore($dictionariesMaps);
  const orgsMap = useStore($participantsMap);
  const { budgetOptions, stageStatusOptions, stagesData } =
    prepareProjectStagesDictionariesData(dictionariesStore);
  const pausePhase = useUnit(pausePhaseEv);
  const completePhase = useUnit(completePhaseEv);
  const inProgressPhase = useUnit(inProgressPhaseEv);
  const pausePhasePending = useUnit(pausePhaseFx.pending);
  const completePhasePending = useUnit(completePhaseFx.pending);

  const [stagesOptions, setStagesOptions] = useState(stagesData);

  const onFinish = (values) => {
    const mutatedData = {
      stage_1: values.stage_1 ?? stages.stage_1,
      stage_2: values.stage_2 ?? stages.stage_2,
      stage_3: values.stage_3 ?? stages.stage_3,
      // stage_4: values.stage_4 || stages.stage_4,
    };
    // if (values.phase_1_status === 'COMPLETED') delete mutatedData.stage_1;
    // if (values.phase_2_status === 'COMPLETED') delete mutatedData.stage_2;
    // if (values.phase_3_status === 'COMPLETED') delete mutatedData.stage_3;
    const payload = { stages: [], phases: [] };
    for (let i = 1; i < 4; i++) {
      payload.phases.push({
        id: phases[i]?.id,
        status: values[`phase_${i}_status`] ?? phases[i].status,
      });
    }

    Object.values(mutatedData).forEach((stage) => {
      if (stage) {
        stage.forEach((item) => {
          payload.stages.push({
            id: item.id || null,
            stage_id: item.stage_id || null,
            responsible_id: item.responsible_id || null,
            role_id: item.role_id ?? null,
            organization_id: item.organization_id ?? null,
            // role_id: item?.responsible_id ? null : item.role_id ?? null,
            // organization_id: item.responsible_id ? null : item.organization_id ?? null,
            status_id: item.status_id || null,
            start_date_plan: item.start_date_plan
              ? item.start_date_plan.format(date_format_short)
              : null,
            start_date_fact: item.start_date_fact
              ? item.start_date_fact.format(date_format_short)
              : null,
            finish_date_plan: item.finish_date_plan
              ? item.finish_date_plan.format(date_format_short)
              : null,
            finish_date_fact: item.finish_date_fact
              ? item.finish_date_fact.format(date_format_short)
              : null,
            note: item.note || '',
            sum_plan: typeof item.sum_plan === 'number' ? item.sum_plan : 0,
            sum_fact: typeof item.sum_fact === 'number' ? item.sum_fact : 0,
            budget_source_ids: item.budget_source_ids || null,
            is_problem: item.is_problem || false,
          });
        });
      }
    });
    editProjectStagesFx({ id, changes: payload });
  };

  const initialValues = {
    stage_1: stages.stage_1 || [],
    stage_2: stages.stage_2 || [],
    stage_3: stages.stage_3 || [],
    // stage_4: stages.stage_4 || [],
  };

  const disabledOptionIds = {
    1: initialValues.stage_1.map((item) => item.stage_id),
    2: initialValues.stage_2.map((item) => item.stage_id),
    3: initialValues.stage_3.map((item) => item.stage_id),
    // 4: initialValues.stage_4.map((item) => item.stage_id),
  };

  const filterOption = (input, option) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  // FIXME Waiting for func rewrite to make proper select options
  const executorOptions =
    participantsTableData?.map((item) => {
      return {
        label: `${rolesMap[item?.role_id] ?? ''} - ${
          orgsMap[item?.organization_id] ?? ''
        }`,
        value: item.id,
      };
    }) ?? [];

  function genExtra(phase) {
    const items = stages[`stage_${phase}`];
    const phaseStatus = phases?.[phase]?.status;

    let startDateFact;
    let startDatePlan;
    let finishDateFact;
    let finishDatePlan;

    if (items && items.length > 0) {
      startDateFact = items[0].start_date_fact;
      finishDateFact = items[0].finish_date_fact;
      startDatePlan = items[0].start_date_plan;
      finishDatePlan = items[0].finish_date_plan;
      items.forEach((item) => {
        if (
          startDateFact &&
          item.start_date_fact &&
          item.start_date_fact.isBefore(startDateFact)
        )
          startDateFact = item.start_date_fact;
        if (
          finishDateFact &&
          item.finish_date_fact &&
          item.finish_date_fact.isAfter(finishDateFact)
        )
          finishDateFact = item.finish_date_fact;
        if (
          startDatePlan &&
          item.start_date_plan &&
          item.start_date_plan.isBefore(startDateFact)
        )
          startDatePlan = item.start_date_plan;
        if (
          finishDatePlan &&
          item.finish_date_plan &&
          item.finish_date_plan.isAfter(finishDateFact)
        )
          finishDatePlan = item.finish_date_plan;
      });
    }

    form.setFieldValue(`phase_${phase}_start_date_plan`, startDatePlan);
    form.setFieldValue(`phase_${phase}_start_date_fact`, startDateFact);
    form.setFieldValue(`phase_${phase}_finish_date_plan`, finishDatePlan);
    form.setFieldValue(`phase_${phase}_finish_date_fact`, finishDateFact);
    form.setFieldValue(`phase_${phase}_status`, phaseStatus);

    return (
      <Space
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Form.Item
          name={`phase_${phase}_start_date_plan`}
          style={{ marginBottom: 'unset' }}
        >
          <DatePicker
            placeholder="Дата начала (план)"
            style={{ width: 180 }}
            disabled
            locale={locale}
          />
        </Form.Item>
        <Form.Item
          name={`phase_${phase}_start_date_fact`}
          style={{ marginBottom: 'unset' }}
        >
          <DatePicker
            placeholder="Дата начала (факт)"
            disabled
            style={{ width: 180 }}
            locale={locale}
          />
        </Form.Item>
        <Form.Item
          name={`phase_${phase}_finish_date_plan`}
          style={{ marginBottom: 'unset' }}
        >
          <DatePicker
            placeholder="Дата завершения (план)"
            disabled
            style={{ width: 180 }}
            locale={locale}
          />
        </Form.Item>
        <Form.Item
          name={`phase_${phase}_finish_date_fact`}
          style={{ marginBottom: 'unset' }}
        >
          <DatePicker
            placeholder="Дата завершения (факт)"
            disabled
            style={{ width: 180 }}
            locale={locale}
          />
        </Form.Item>
        <Form.Item
          name={`phase_${phase}_status`}
          style={{ marginBottom: 'unset' }}
        >
          <Select
            placeholder="Статус"
            style={{ width: 180 }}
            options={phaseStatusDict}
            // onChange={(value) => onChangePhase(value, phase)}
            loading={pausePhasePending || completePhasePending}
            disabled={pausePhasePending || completePhasePending}
          />
        </Form.Item>
      </Space>
    );
  }

  const collapseItems = useMemo(() => {
    const temp = {
      1: {},
      2: {},
      3: {},
    };

    Object.keys(temp).forEach((stage) => {
      let disabled = false;
      if (phases?.[stage]?.status === 'COMPLETED') disabled = true;
      temp[stage] = [
        {
          key: stage,
          label: `Стадия ${stage}`,
          children: (
            <Form.Item>
              <Form.List name={`stage_${stage}`}>
                {(fields, { add, remove }) => {
                  return (
                    <>
                      <div className={style.stages_wrapper}>
                        {fields.map(({ key, name }) => (
                          <div className={style.stage_form} key={key}>
                            <Form.Item name={[name, 'id']} noStyle>
                              <Input type="hidden" disabled={disabled} />
                            </Form.Item>
                            <Form.Item
                              name={[name, 'stage_id']}
                              label="Этап"
                              rules={[
                                {
                                  required: true,
                                  message: 'Пожалуйста выберите этап',
                                },
                                {
                                  validator: (_, value) =>
                                    value
                                      ? Promise.resolve()
                                      : Promise.reject(),
                                },
                              ]}
                            >
                              <StageSelect
                                filterOption={filterOption}
                                showSearch
                                options={stagesOptions[stage]}
                                disabled={disabled}
                                // onChange={onSelectChange}
                              />
                            </Form.Item>
                            <Divider
                              orientation="left"
                              orientationMargin={0}
                              plain
                            >
                              Ответственный:
                            </Divider>
                            <ResponsibleSelects
                              name={name}
                              stage={`stage_${stage}`}
                              form={form}
                              filterOption={filterOption}
                              executorOptions={executorOptions}
                              disabled={disabled}
                            />
                            <Divider />
                            <Form.Item
                              name={[name, 'start_date_plan']}
                              label="Дата начала (план)"
                            >
                              <DatePicker locale={locale} disabled={disabled} />
                            </Form.Item>
                            <Form.Item
                              name={[name, 'start_date_fact']}
                              label="Дата начала (факт)"
                            >
                              <DatePicker locale={locale} disabled={disabled} />
                            </Form.Item>
                            <Form.Item
                              name={[name, 'finish_date_plan']}
                              label="Дата завершения (план)"
                            >
                              <DatePicker locale={locale} disabled={disabled} />
                            </Form.Item>
                            <Form.Item
                              name={[name, 'finish_date_fact']}
                              label="Дата завершения (факт)"
                            >
                              <DatePicker locale={locale} disabled={disabled} />
                            </Form.Item>
                            <Form.Item
                              name={[name, 'status_id']}
                              label="Статус"
                            >
                              <Select
                                filterOption={filterOption}
                                showSearch
                                options={stageStatusOptions}
                                disabled={disabled}
                              />
                            </Form.Item>
                            <Form.Item name={[name, 'note']} label="Примечание">
                              <Input disabled={disabled} />
                            </Form.Item>
                            <Form.Item
                              name={[name, 'sum_plan']}
                              label="Бюджет (план)"
                            >
                              <InputNumber
                                type="number"
                                controls={false}
                                disabled={disabled}
                              />
                            </Form.Item>
                            <Form.Item
                              name={[name, 'sum_fact']}
                              label="Бюджет (факт)"
                            >
                              <InputNumber
                                type="number"
                                controls={false}
                                disabled={disabled}
                              />
                            </Form.Item>
                            <Form.Item
                              name={[name, 'budget_source_ids']}
                              label="Источник финансирования"
                              rules={[
                                {
                                  required: true,
                                  message:
                                    'Пожалуйста выберите источник финансирования',
                                },
                              ]}
                            >
                              <Select
                                filterOption={filterOption}
                                showSearch
                                options={budgetOptions}
                                mode="multiple"
                                dropdownStyle={{ width: 500 }}
                                disabled={disabled}
                              />
                            </Form.Item>
                            <Form.Item
                              name={[name, 'is_problem']}
                              label="Проблемный"
                              valuePropName="checked"
                            >
                              <Checkbox disabled={disabled} />
                            </Form.Item>
                            <Button
                              icon={
                                <CloseOutlined
                                  style={{ fontSize: '14px' }}
                                  onClick={() => {
                                    remove(name);
                                  }}
                                />
                              }
                              shape="circle"
                              className={style.delete_button}
                              disabled={disabled}
                            />
                          </div>
                        ))}
                      </div>
                      <Button
                        type="dashed"
                        disabled={disabled}
                        onClick={() => {
                          // if (fields.length !== stagesOptions[stage].length) {
                          add();
                          // }
                        }}
                        block
                      >
                        + Этап
                      </Button>
                    </>
                  );
                }}
              </Form.List>
            </Form.Item>
          ),
          extra: genExtra(stage),
        },
      ];
    });
    return temp;
  }, [stages, phases]);

  // const onChangePhase = (value, phase) => {
  //   if (value === 'IN_PROGRESS') {
  //     inProgressPhase({
  //       project_id: id,
  //       phase_id: phases[phase].id,
  //     });
  //   }
  //   if (value === 'COMPLETED')
  //     completePhase({
  //       project_id: id,
  //       phase_id: phases[phase].id,
  //     });
  //   if (value === 'PAUSED')
  //     pausePhase({
  //       project_id: id,
  //       phase_id: phases[phase].id,
  //     });
  // };

  const onFieldsChange = (field, allFields) => {
    const phaseFieldsTriggers = [
      'start_date_plan',
      'start_date_fact',
      'finish_date_plan',
      'finish_date_fact',
    ];
    if (
      field[0].name.length === 3 &&
      phaseFieldsTriggers.some((item) => field[0].name.includes(item))
    ) {
      const phase = field[0].name[0][6];
      const fieldName = field[0].name[2];
      const filteredValues = allFields.filter(
        (item) => item.name.length === 3 && item.name.includes(fieldName)
      );
      let value;
      filteredValues.forEach((item) => {
        if (fieldName.includes('finish')) {
          if (!value) {
            value = item.value;
          } else if (item.value && item.value.isAfter(value))
            value = item.value;
        } else if (!value) {
          value = item.value;
        } else if (item.value && item.value.isBefore(value)) value = item.value;
      });
      form.setFieldValue(`phase_${phase}_${fieldName}`, value);
    }
    // if (field[0].name.length === 1 && /stage_\d{1}/.test(field[0].name[0])) {
    //   const stage = field[0].name[0].split('_')[1];
    //   const existingStages = field[0].value
    //     .filter((item) => item !== undefined && item.stage_id !== undefined)
    //     .map((item) => item.stage_id);
    //   setStagesOptions({
    //     ...stagesOptions,
    //     [stage]: stagesOptions[stage].map((item) => {
    //       if (existingStages.includes(item.value)) {
    //         return {
    //           ...item,
    //           disabled: true,
    //         };
    //       }
    //       return {
    //         ...item,
    //         disabled: false,
    //       };
    //     }),
    //   });
    // }
  };
  return (
    <Form
      onFinish={onFinish}
      initialValues={initialValues}
      form={form}
      onFieldsChange={onFieldsChange}
    >
      <Collapse items={collapseItems[1]} className={style.collapse} />
      <Collapse items={collapseItems[2]} className={style.collapse} />
      <Collapse items={collapseItems[3]} className={style.collapse} />
      {/* <Collapse items={collapseItems[4]} className={style.collapse} /> */}
      <Button htmlType="submit" type="primary" loading={isLoading}>
        Подтвердить
      </Button>
    </Form>
  );
}

export default EditSteps;
